import { ToastProvider } from "@breakout/ui/components/Toast"
import {
	createRootRoute,
	type ErrorComponentProps,
	Link,
	Outlet,
	useRouter,
} from "@tanstack/react-router"
import { AlertCircle, Home, RefreshCw } from "lucide-react"
import { lazy, Suspense, useEffect } from "react"
import { ThemeProvider } from "../components/theme/ThemeProvider"

// Lazy load devtools only in development
const TanStackRouterDevtools = import.meta.env.DEV
	? lazy(() =>
			import("@tanstack/router-devtools").then(mod => ({
				default: mod.TanStackRouterDevtools,
			})),
		)
	: () => null

import { PageLayout } from "../components/layout/PageLayout"
import { Sentry } from "../lib/sentry"

export const Route = createRootRoute({
	component: RootComponent,
	errorComponent: RootErrorComponent,
	notFoundComponent: NotFoundComponent,
})

function RootComponent() {
	return (
		<ThemeProvider defaultTheme="system" storageKey="breakout-ui-theme">
			<ToastProvider>
				<PageLayout>
					<Outlet />
				</PageLayout>
				<Suspense fallback={null}>
					<TanStackRouterDevtools position="bottom-right" />
				</Suspense>
			</ToastProvider>
		</ThemeProvider>
	)
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
	const router = useRouter()

	// Report error to Sentry
	useEffect(() => {
		if (error instanceof Error) {
			Sentry.captureException(error, {
				tags: { errorBoundary: "root" },
			})
			console.error("[RootError]", error)
		}
	}, [error])

	return (
		<ToastProvider>
			<PageLayout>
				<div className="flex min-h-[60vh] flex-col items-center justify-center px-4">
					<div className="mx-auto max-w-md text-center">
						<div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-full bg-red-100">
							<AlertCircle aria-hidden="true" className="size-8 text-red-600" />
						</div>
						<h1 className="mb-2 text-balance font-bold text-2xl text-gray-900">
							Something went wrong
						</h1>
						<p className="mb-6 text-gray-600">
							{error instanceof Error ? error.message : "An unexpected error occurred"}
						</p>
						<div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
							<button
								className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 px-6 py-3 font-medium text-white transition-colors hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
								onClick={() => {
									reset()
									router.invalidate()
								}}
								type="button"
							>
								<RefreshCw aria-hidden="true" className="size-4" />
								Try again
							</button>
							<Link
								className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-6 py-3 font-medium text-gray-900 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
								to="/"
							>
								<Home aria-hidden="true" className="size-4" />
								Go home
							</Link>
						</div>
						{import.meta.env.DEV && error instanceof Error && error.stack && (
							<details className="mt-8 text-left">
								<summary className="cursor-pointer text-gray-500 text-sm hover:text-gray-700">
									Error details
								</summary>
								<pre className="mt-2 overflow-auto rounded-lg bg-gray-100 p-4 text-gray-700 text-xs">
									{error.stack}
								</pre>
							</details>
						)}
					</div>
				</div>
			</PageLayout>
		</ToastProvider>
	)
}

function NotFoundComponent() {
	return (
		<div className="flex min-h-[60vh] flex-col items-center justify-center px-4">
			<div className="mx-auto max-w-md text-center">
				<h1 className="mb-2 text-balance font-bold text-6xl text-gray-900">404</h1>
				<h2 className="mb-4 text-balance font-semibold text-gray-700 text-xl">Page not found</h2>
				<p className="mb-8 text-gray-600">
					The page you're looking for doesn't exist or has been moved.
				</p>
				<Link
					className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 px-6 py-3 font-medium text-white transition-colors hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
					to="/"
				>
					<Home aria-hidden="true" className="size-4" />
					Back to home
				</Link>
			</div>
		</div>
	)
}
